import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { DateService, getYear, Store } from '@softline/core';
import {
  BreakpointService,
  MessageBarService,
  ModalContainerComponent,
} from '@softline/ui-core';
import { filter } from 'rxjs';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  AppearanceService,
  AppInfoStore,
  SOFTLINE_FEATURE_APP_INFO,
} from '@softline/application';
import { ShellEnvironment } from '../../shell.constants';
import {
  SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING,
  SOFTLINE_TOKEN_SHELL_ENVIRONMENT,
} from '../../shell.tokens';

@Component({
  selector: 'soft-public-shell',
  templateUrl: './public-shell.component.html',
  styleUrls: ['./public-shell.component.css'],
  hostDirectives: [CdkScrollable],
  imports: [RouterModule, ModalContainerComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: SOFTLINE_TOKEN_SHELL_ENVIRONMENT, useValue: ShellEnvironment.PUBLIC },
  ],
})
export class PublicShellComponent implements OnInit {
  year?: string;
  messageBarService = inject(MessageBarService);
  private router = inject(Router);
  protected appearanceService = inject(AppearanceService);
  private readonly breakpointService = inject(BreakpointService);

  version = this.store.signal(SOFTLINE_FEATURE_APP_INFO, AppInfoStore.getters.version, {
    initialValue: '0',
  });

  appName = this.store.signal(SOFTLINE_FEATURE_APP_INFO, AppInfoStore.getters.name, {
    initialValue: 'SoftApps',
  });

  readonly showBranding = inject(SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING);

  readonly brandingSrc = signal<string | null>(null);
  readonly isReturningUser = localStorage.getItem('softapps-visited') === 'true';

  readonly isMobile = this.breakpointService.isMobile;

  logoSpinning = signal(false);
  rotationDegrees = signal(360);

  constructor(
    private store: Store,
    private dateService: DateService
  ) {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe(() => this.logoSpinning.set(true));
  }

  ngOnInit(): void {
    this.year = '' + getYear(this.dateService.now());
    localStorage.setItem('softapps-visited', 'true');
    if (this.showBranding) {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--publicShellBranding')
        .trim();
      const match = raw.match(/url\(['"]?([^'")\s]+)['"]?\)/);
      if (match?.[1]) this.brandingSrc.set(match[1]);
    }
  }

  spinLogo(): void {
    if (this.logoSpinning()) return;
    this.rotationDegrees.update((d) => (d === 360 ? -360 : 360));
    this.logoSpinning.set(true);
  }

  onLogoAnimationEnd(): void {
    this.logoSpinning.set(false);
  }
}
